import React, { useEffect } from 'react';
import {
  Box,
  Heading,
  MultiSelectField,
  SingleSelectField,
  useAnalytics,
  useWatch,
} from '@medely/ui-kit';
import { YEARS_OPTIONS } from '../constants';
import { IPosition } from '@medely/types';
import { SelectedPosition } from '../types';
import { useCurrentUser } from '@mp/hooks';

type PositionItemProps = {
  position?: Partial<IPosition>;
  positionIndex: number;
};

const PositionItem = ({ position, positionIndex }: PositionItemProps) => {
  const specialtyOptions =
    position?.specialties
      ?.filter(({ active }) => active)
      .map(({ id, label }) => ({ value: id.toString(), label })) || [];

  return (
    <Box mt={2}>
      <Heading
        size="xs"
        color="text.primary"
        testId="clinical-experience-and-specialties-position-title"
      >
        {position?.display_name}
      </Heading>
      <Box>
        <Box mt={2} testId="clinical-experience-and-specialties-single-select-field">
          <SingleSelectField
            name={`professional_positions.positions[${positionIndex}].years_of_experience`}
            label="Years of experience"
            options={YEARS_OPTIONS}
          />
        </Box>
        {!!specialtyOptions.length && (
          <Box mt={2}>
            <MultiSelectField
              name={`professional_positions.positions[${positionIndex}].specialty_ids`}
              label="Specialties"
              options={specialtyOptions}
              testId="clinical-experience-and-specialties-multi-select"
              placeholder=""
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ClinicalExperienceAndSpecialties = ({
  allPositions,
}: {
  allPositions: Partial<IPosition>[];
}) => {
  const analytics = useAnalytics();
  const { currentUser, proType } = useCurrentUser();

  useEffect(() => {
    if (!!currentUser) {
      analytics.track('Product Application Positions Background - Viewed', {
        account_id: currentUser?.id,
        context: 'Product Application Positions Background',
        subcontext: null,
        action: 'Viewed',
        pro_type: proType,
      });
    }
  }, [currentUser]);

  const professionalPositions = useWatch({ name: 'professional_positions' });
  const selectedPositions: Partial<IPosition>[] =
    professionalPositions?.positions.map((pos: SelectedPosition) =>
      allPositions.find((p) => p.id === pos.id),
    ) || [];

  return (
    <>
      <Heading size="s" color="text.primary" testId="clinical-experience-and-specialties-title">
        Select years of clinical experience and specialties
      </Heading>
      {selectedPositions
        ?.sort((a, b) =>
          (a?.display_name || '').localeCompare(b?.display_name || '', undefined, {
            sensitivity: 'base',
          }),
        )
        ?.map((position, idx) => (
          <PositionItem key={position?.id} position={position} positionIndex={idx} />
        ))}
    </>
  );
};

export default ClinicalExperienceAndSpecialties;
